import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'
import { Menu, SquareChevronLeft } from 'lucide-react'
import { type ReactNode, createContext, use, useCallback, useEffect, useMemo, useState } from 'react'

import { cn } from '../../../lib/utils.ts'
import { Button, buttonVariants } from '../button/button.tsx'
import { Drawer } from '../drawer/drawer.tsx'
import { Skeleton } from '../skeleton/skeleton.tsx'

import { sidebarMenuButtonVariants, sidebarMenuRowLabelHeight, sidebarMenuRowPadding } from './sidebar.variants.ts'

type SidebarLabels = {
  /** Accessible name for the mobile trigger that opens the nav drawer. */
  openMenu: string
  /** Accessible name for the drawer header's close control. */
  closeMenu: string
}

// The design system has no access to the app's translations (see `@orus.eu/pharaoh-next`'s
// dependencies), so these French defaults exist only to keep Storybook stories and bare-provider
// tests working; a real app always supplies its own translated `labels`.
const DEFAULT_SIDEBAR_LABELS: SidebarLabels = {
  openMenu: 'Ouvrir le menu',
  closeMenu: 'Fermer le menu',
}

type SidebarContextValue = {
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  labels: SidebarLabels
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

function useSidebar(): SidebarContextValue {
  const ctx = use(SidebarContext)
  if (ctx === null) throw new Error('useSidebar must be used within Sidebar.Provider')
  return ctx
}

// `View.Header` renders the trigger unconditionally, so it must be able to sit in a `View` mounted
// with no navigation at all (stories, tests, screens outside the customer shell) and render nothing.
function useOptionalSidebar(): SidebarContextValue | null {
  return use(SidebarContext)
}

const MOBILE_QUERY = '(max-width: 1023px)'

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches)
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY)
    const handleChange = () => setIsMobile(query.matches)
    handleChange()
    query.addEventListener('change', handleChange)
    return () => query.removeEventListener('change', handleChange)
  }, [])
  return isMobile
}

function SidebarProvider(props: SidebarProvider.Props) {
  const { children, labels = DEFAULT_SIDEBAR_LABELS } = props
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = useState(false)

  const toggleSidebar = useCallback(() => {
    setOpenMobile((previous) => !previous)
  }, [])

  const value = useMemo<SidebarContextValue>(
    () => ({ openMobile, setOpenMobile, isMobile, toggleSidebar, labels }),
    [openMobile, isMobile, toggleSidebar, labels],
  )

  return <SidebarContext value={value}>{children}</SidebarContext>
}
namespace SidebarProvider {
  export type Props = {
    children?: ReactNode
    /** Accessible names for the mobile trigger and close control. @default French copy */
    labels?: SidebarLabels
  }
  export type Labels = SidebarLabels
}

/**
 * Desktop: a static `<nav>` filling the `Layout.Nav` column. Mobile: a `Drawer` sliding in from the
 * left edge, carrying the same nav content. The column chrome (width, border) lives on `Layout.Nav`.
 * Requires `Sidebar.Provider` for the mobile drawer state.
 *
 * `data-mobile="true"` identifies the mobile drawer.
 *
 * @summary Responsive navigation with a mobile drawer.
 */
function SidebarRoot(props: SidebarRoot.Props) {
  const { className, children, 'aria-label': ariaLabel, ...rest } = props
  const { openMobile, setOpenMobile, isMobile } = useSidebar()

  if (isMobile) {
    return (
      <Drawer.Root open={openMobile} onOpenChange={setOpenMobile} swipeDirection="left">
        <Drawer.Portal>
          <Drawer.Backdrop />
          <Drawer.Viewport>
            <Drawer.Popup
              data-slot="sidebar"
              data-mobile="true"
              aria-label={ariaLabel}
              render={<nav />}
              // `popupVariants` drives the panel width through `--drawer-content-width`, which is 100%
              // below `md`. Figma keeps the page visible to the right of the nav, so the panel is capped
              // instead: a `max-width` bounds the same box without fighting the base rule's `:is()`
              // specificity, and collapses back to full width on a 320px-wide screen.
              className={cn(
                'max-w-xs border-r border-line-base-basic bg-surface-base-normal-subtle',
                '[--drawer-bleed-background:var(--color-surface-base-normal-subtle)]',
                className,
              )}
            >
              <Drawer.Content className="gap-80 pt-90 pl-70">{children}</Drawer.Content>
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    )
  }

  return (
    <nav
      data-slot="sidebar"
      aria-label={ariaLabel}
      className={cn('flex min-h-0 flex-1 flex-col gap-80 overflow-hidden pt-80 pl-70', className)}
      {...rest}
    >
      {children}
    </nav>
  )
}
namespace SidebarRoot {
  export type Props = React.ComponentProps<'nav'>
}

function SidebarHeader(props: SidebarHeader.Props) {
  const { className, children, ...rest } = props
  const { isMobile, labels } = useSidebar()
  return (
    <div
      data-slot="sidebar-header"
      className={cn(
        'relative z-10 flex flex-col gap-20 bg-surface-base-normal-subtle pr-70',
        isMobile && 'flex-row items-center justify-between',
        className,
      )}
      {...rest}
    >
      {children}
      {/* Swipe-to-dismiss alone does not satisfy WCAG 2.5.7: a dragging gesture needs a single-pointer
          alternative. Hidden on desktop where the sidebar stays in flow and has nothing to close. */}
      {isMobile && (
        <Drawer.Close
          render={
            <Button
              type="button"
              variant="text"
              size="icon-medium"
              aria-label={labels.closeMenu}
              className="size-[24px]"
            />
          }
        >
          <SquareChevronLeft aria-hidden="true" className="size-5" />
        </Drawer.Close>
      )}
    </div>
  )
}
namespace SidebarHeader {
  export type Props = React.ComponentProps<'div'>
}

function SidebarScrollPort(props: SidebarScrollPort.Props) {
  const { className, onScroll, ref, children, ...rest } = props
  const [isScrolled, setIsScrolled] = useState(false)
  const setScrollPortRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        if (typeof ref === 'function') ref(null)
        else if (ref) ref.current = null
        return
      }

      const handleScroll = () => setIsScrolled(node.scrollTop > 0)
      node.addEventListener('scroll', handleScroll, { passive: true })

      const refCleanup = typeof ref === 'function' ? ref(node) : undefined
      if (ref && typeof ref !== 'function') ref.current = node

      return () => {
        node.removeEventListener('scroll', handleScroll)
        refCleanup?.()
        if (typeof ref === 'function' && !refCleanup) ref(null)
        else if (ref && typeof ref !== 'function') ref.current = null
      }
    },
    [ref],
  )
  return (
    <div data-slot="sidebar-scroll-port" className={cn('relative flex min-h-0 flex-1 flex-col', className)} {...rest}>
      {/* The frame pins the fade; the scroll element keeps the right-edge scrollbar, while its top/left paint inset and
          the wrapper's right/bottom gutters preserve focus rings without shifting rows. */}
      <div
        data-slot="sidebar-scroll-element"
        className="-mt-20 -ml-20 flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto pt-20 pl-20"
        ref={setScrollPortRef}
        onScroll={onScroll}
      >
        <div
          data-slot="sidebar-content-wrapper"
          className="
            flex min-h-full min-w-0 flex-1 flex-col gap-80 pr-70 pb-80
            max-md:pb-90
          "
        >
          {children}
        </div>
      </div>
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -inset-x-[4px] -top-[4px] z-10 h-[16px]',
          'bg-[linear-gradient(to_bottom,var(--color-bg-base-normal-subtle),transparent)]',
          `
            opacity-0 transition-opacity motion-color
            data-[scrolled]:opacity-100
          `,
        )}
        data-scrolled={isScrolled ? '' : undefined}
      />
    </div>
  )
}
namespace SidebarScrollPort {
  export type Props = React.ComponentProps<'div'>
}

function SidebarContent(props: SidebarContent.Props) {
  const { className, ...rest } = props
  return <div data-slot="sidebar-content" className={cn('flex flex-col gap-50', className)} {...rest} />
}
namespace SidebarContent {
  export type Props = React.ComponentProps<'div'>
}

function SidebarFooter(props: SidebarFooter.Props) {
  const { className, ...rest } = props
  return <div data-slot="sidebar-footer" className={cn('mt-auto flex flex-col gap-60 pb-90', className)} {...rest} />
}
namespace SidebarFooter {
  export type Props = React.ComponentProps<'div'>
}

function SidebarGroup(props: SidebarGroup.Props) {
  const { label, className, children, ...rest } = props
  return (
    <div data-slot="sidebar-group" className={cn('flex w-full flex-col gap-10', className)} {...rest}>
      {label !== undefined && (
        <div data-slot="sidebar-group-label" className="px-40 py-20 text-10 font-500 text-content-base-secondary">
          {label}
        </div>
      )}
      {children}
    </div>
  )
}
namespace SidebarGroup {
  export type Props = React.ComponentProps<'div'> & {
    /** Optional section label. */
    label?: ReactNode
  }
}

function SidebarMenu(props: SidebarMenu.Props) {
  const { className, ...rest } = props
  return <ul data-slot="sidebar-menu" className={cn('flex w-full flex-col gap-20', className)} {...rest} />
}
namespace SidebarMenu {
  export type Props = React.ComponentProps<'ul'>
}

function SidebarMenuItem(props: SidebarMenuItem.Props) {
  const { className, ...rest } = props
  return <li data-slot="sidebar-menu-item" className={cn('list-none', className)} {...rest} />
}
namespace SidebarMenuItem {
  export type Props = React.ComponentProps<'li'>
}

type SidebarMenuItemVariants = NonNullable<VariantProps<typeof sidebarMenuButtonVariants>>

function SidebarNavigationLink(props: SidebarNavigationLink.Props) {
  const { dismissOnMobile = false, onClick, render, ref, ...rest } = props
  const { isMobile, setOpenMobile } = useSidebar()

  const handleClick: NonNullable<React.ComponentProps<'a'>['onClick']> = (event) => {
    onClick?.(event)
    const isUnmodifiedPrimaryClick =
      event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    if (dismissOnMobile && isMobile && isUnmodifiedPrimaryClick && !event.defaultPrevented) setOpenMobile(false)
  }

  return useRender({
    render,
    ref,
    defaultTagName: 'a',
    props: { onClick: handleClick, ...rest },
  })
}
namespace SidebarNavigationLink {
  export type Props = useRender.ComponentProps<'a'> & {
    dismissOnMobile?: boolean
  }
}

function SidebarMenuLink(props: SidebarMenuLink.Props) {
  const { icon, active, density, className, children, dismissOnMobile = true, ...rest } = props

  return (
    <SidebarNavigationLink
      dismissOnMobile={dismissOnMobile}
      data-slot="sidebar-menu-link"
      data-active={active ? true : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(buttonVariants({ variant: 'text' }), sidebarMenuButtonVariants({ density, className }))}
      {...rest}
    >
      <>
        {icon}
        <span>{children}</span>
      </>
    </SidebarNavigationLink>
  )
}
namespace SidebarMenuLink {
  export type Props = useRender.ComponentProps<'a'> & {
    icon?: ReactNode
    active?: boolean
    density?: NonNullable<SidebarMenuItemVariants['density']> | undefined
    dismissOnMobile?: boolean
  }
}

function SidebarMenuButton(props: SidebarMenuButton.Props) {
  const { icon, active, density, className, children, onClick, ...rest } = props
  const { isMobile, setOpenMobile } = useSidebar()

  // On mobile the menu lives in the drawer; selecting an item should dismiss it.
  const handleClick: NonNullable<Button.Props['onClick']> = (event) => {
    onClick?.(event)
    if (isMobile) setOpenMobile(false)
  }

  return (
    <Button
      data-slot="sidebar-menu-button"
      data-active={active ? true : undefined}
      variant="text"
      onClick={handleClick}
      className={cn(sidebarMenuButtonVariants({ density, className }))}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </Button>
  )
}
namespace SidebarMenuButton {
  export type Props = Button.Props & {
    /** Leading icon. */
    icon?: ReactNode
    /** Marks the item active (in addition to TanStack Router's `data-status=active`). */
    active?: boolean
    /** Row height. `compact` is the footer shape. @default 'default' */
    density?: NonNullable<SidebarMenuItemVariants['density']> | undefined
  }
}

function SidebarTrigger(props: SidebarTrigger.Props) {
  const { className, ...rest } = props
  const sidebar = useOptionalSidebar()
  if (sidebar === null) return null

  return (
    <Button
      type="button"
      variant="text"
      size="icon-medium"
      aria-label={sidebar.labels.openMenu}
      onClick={sidebar.toggleSidebar}
      className={className}
      {...rest}
    >
      <Menu aria-hidden="true" className="size-[24px]" />
    </Button>
  )
}
namespace SidebarTrigger {
  export type Props = React.ComponentProps<typeof Button>
}

// The label bar stops short of the row instead of filling it, because a nav label does: a full-width
// bar reads as a table of rows rather than as a menu. It carries no layout, so it can't shift anything.
const SKELETON_LABEL_WIDTH = '60%'

// Geometry mirrors SidebarMenu (`gap-20`) and SidebarMenuButton — padding from `sidebarMenuRowPadding`,
// label height from `sidebarMenuRowLabelHeight`, `size-4` icon — so rows occupy the exact same box and
// nothing shifts when the real menu replaces the skeleton. `compact` drops the icon, like the row does.
function SidebarMenuSkeleton(props: SidebarMenuSkeleton.Props) {
  const { count = 5, density = 'default', className, ...rest } = props
  return (
    <ul
      data-slot="sidebar-menu-skeleton"
      aria-hidden
      className={cn('flex w-full flex-col gap-20', className)}
      {...rest}
    >
      {Array.from({ length: count }, (_, index) => (
        <li
          key={index}
          data-slot="sidebar-menu-skeleton-item"
          data-density={density}
          className={cn('flex h-auto w-full list-none items-center gap-40 rounded-20', sidebarMenuRowPadding[density])}
        >
          {density === 'default' && <Skeleton variant="block" className="size-4 shrink-0 rounded-10" />}
          <Skeleton variant="text" width={SKELETON_LABEL_WIDTH} height={sidebarMenuRowLabelHeight} />
        </li>
      ))}
    </ul>
  )
}
namespace SidebarMenuSkeleton {
  export type Props = React.ComponentProps<'ul'> & {
    /** Number of skeleton rows. @default 5 */
    count?: number
    /** Row height, mirroring `MenuButton`. `compact` is the footer shape. @default 'default' */
    density?: keyof typeof sidebarMenuRowPadding | undefined
  }
}

const Sidebar = {
  Root: SidebarRoot,
  Provider: SidebarProvider,
  Header: SidebarHeader,
  ScrollPort: SidebarScrollPort,
  Content: SidebarContent,
  Footer: SidebarFooter,
  Group: SidebarGroup,
  Menu: SidebarMenu,
  MenuItem: SidebarMenuItem,
  NavigationLink: SidebarNavigationLink,
  MenuLink: SidebarMenuLink,
  MenuButton: SidebarMenuButton,
  Trigger: SidebarTrigger,
  MenuSkeleton: SidebarMenuSkeleton,
}

namespace Sidebar {
  export namespace Root {
    export type Props = SidebarRoot.Props
  }
  export namespace Provider {
    export type Props = SidebarProvider.Props
    export type Labels = SidebarProvider.Labels
  }
  export namespace Header {
    export type Props = SidebarHeader.Props
  }
  export namespace ScrollPort {
    export type Props = SidebarScrollPort.Props
  }
  export namespace Content {
    export type Props = SidebarContent.Props
  }
  export namespace Footer {
    export type Props = SidebarFooter.Props
  }
  export namespace Group {
    export type Props = SidebarGroup.Props
  }
  export namespace Menu {
    export type Props = SidebarMenu.Props
  }
  export namespace MenuItem {
    export type Props = SidebarMenuItem.Props
  }
  export namespace NavigationLink {
    export type Props = SidebarNavigationLink.Props
  }
  export namespace MenuLink {
    export type Props = SidebarMenuLink.Props
  }
  export namespace MenuButton {
    export type Props = SidebarMenuButton.Props
  }
  export namespace Trigger {
    export type Props = SidebarTrigger.Props
  }
  export namespace MenuSkeleton {
    export type Props = SidebarMenuSkeleton.Props
  }
}

export { Sidebar, useSidebar }
